import React, { ChangeEvent, PropsWithChildren } from 'react';

import {
  Title,
  RadioButton,
  Checkbox,
  Button,
  Tooltip,
  Icon
} from '@gerdau/hefesto';
import { Chips } from '@gerdau/hefesto-mais-web';
import {
  CheckboxContainer,
  ClearAllButtonContainer,
  FilterContainer,
  FilterHeader,
  FiltersContainer,
  FilterTagsContainer,
  FilterTitle
} from './styles';
import { TChipArray } from '@gerdau/hefesto-mais-web/Chips/Chips.types';
import {
  ProductType,
  SearchOptionsProps,
  SortProductsProps
} from '../../../services/MVP/models';
import { RadioButtonContainer } from './RadioGroup/style';
import { ApplicationState } from '../../../store';
import { useSelector } from 'react-redux';
import useDebounce from '../../../hooks/useDebounce';

const codeRadioButtons = [
  {
    value: 'gerdauCode',
    label: 'Código Gerdau'
  },
  {
    value: 'internalCode',
    label: 'Código Interno'
  }
];

const orderRadioButtons = [
  {
    value: 'R',
    label: 'Relevância'
  },
  {
    value: 'AC',
    label: 'Acordo de preço'
  },
  {
    value: 'AB',
    label: 'Ordem alfabética'
  }
];

export interface FiltesProps {
  handleSearchChange: (
    value: SearchOptionsProps['filters'],
    noChanges?: boolean
  ) => Promise<void>;
  handleSortChange: (order: SortProductsProps['sortType']) => void;
  searchOptions: SearchOptionsProps;
  productsTypesList: ProductType[];
  sortLoading: boolean;
}
const Filters = ({
  handleSearchChange,
  handleSortChange,
  searchOptions,
  productsTypesList,
  sortLoading
}: FiltesProps) => {
  const {
    loading,
    loadingProductDescription,
    loadingPurchaseReceivers,
    loadingBlocks,
    loadingCustomerById
  } = useSelector((state: ApplicationState) => state.customers);

  const customerLoading =
    loading ||
    !!loadingProductDescription ||
    !!loadingPurchaseReceivers ||
    !!loadingBlocks ||
    !!loadingCustomerById;

  const pageLoading = sortLoading || customerLoading;

  const buildChips = (): TChipArray[] => {
    const chips = [];
    if (searchOptions.filters.input) {
      chips.push({
        label: `Busca por "${searchOptions.filters.input}"`,
        id: 'search-query',
        tooltipMessage: searchOptions.filters.input,
        tooltipPlacement: 'top'
      } as TChipArray);
    }

    searchOptions.filters.productsTypes
      ?.filter((f) => f.length > 0)
      .forEach((f) =>
        chips.push({
          id: f,
          label: productsTypesList.find((type) => type.key === f)?.description
        })
      );

    return chips;
  };

  const handleOnCloseTag = async (value: string) => {
    if (value === 'search-query') {
      await handleSearchChange({
        ...searchOptions.filters,
        input: ''
      });
      return;
    }

    await handleSearchChange({
      ...searchOptions.filters,
      productsTypes: searchOptions.filters.productsTypes?.filter(
        (f) => f !== value
      )
    });
  };

  const handleClick = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'code' | 'sort' | 'types'
  ) => {
    const value = e.target.value;
    if (value && !pageLoading) {
      await handleSearchChange(
        {
          ...searchOptions.filters,
          ...(type === 'code' && {
            materialCode: value as SearchOptionsProps['filters']['materialCode']
          })
        },
        type === 'code'
      );
    }
  };

  const shoundFilterBeChecked = (id: string) => {
    return searchOptions.filters.productsTypes?.some((f) => f === id);
  };

  const handleFilterChange = async (
    e: ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    if (e.target.checked) {
      await handleSearchChange({
        ...searchOptions.filters,
        productsTypes: [
          ...(searchOptions.filters.productsTypes
            ? searchOptions.filters.productsTypes
            : []),
          value
        ].filter((e) => e.length > 0)
      });
    } else {
      await handleSearchChange({
        ...searchOptions.filters,
        productsTypes: searchOptions.filters.productsTypes?.filter(
          (f) => f !== value
        )
      });
    }
  };

  const handleClearFilters = async () => {
    await handleSearchChange({
      materialCode: 'gerdauCode',
      ordenation: 'R',
      productsTypes: [],
      input: ''
    });
  };

  const showChips =
    [
      searchOptions.filters.input,
      ...(searchOptions.filters.productsTypes
        ? searchOptions.filters.productsTypes
        : [])
    ].filter((f) => f && f.length > 0).length > 0;

  const debouncedChange = useDebounce(handleSortChange, 300);

  const handleDebounced = (e: SortProductsProps['sortType']) =>
    debouncedChange(e);

  return (
    <FiltersContainer>
      <FilterHeader>
        <Title level={2}>Filtros</Title>
        {showChips && (
          <>
            <FilterTagsContainer>
              <Chips
                chips={buildChips()}
                type="input"
                handleRemove={handleOnCloseTag}
              />
            </FilterTagsContainer>
            <ClearAllButtonContainer>
              <Button
                onClick={handleClearFilters}
                name="clean-all-filters"
                variant="tertiary"
                fullWidth
              >
                Limpar filtros
              </Button>
            </ClearAllButtonContainer>
          </>
        )}
      </FilterHeader>
      <FilterSection title="Código do material">
        {codeRadioButtons.map((radioBtn) => (
          <RadioButtonContainer key={radioBtn.value}>
            <RadioButton
              name={radioBtn.value}
              id={radioBtn.value}
              value={radioBtn.value}
              onChange={(e) =>
                radioBtn.value !== searchOptions.filters.materialCode &&
                handleClick(e, 'code')
              }
              label={radioBtn.label}
              checked={radioBtn.value === searchOptions.filters.materialCode}
            />
            {radioBtn.value === 'internalCode' && (
              <Tooltip
                title="Este é o seu código interno de produto cadastrado na Gerdau.Se você não houver cadastrado este código, apresentaremos o código padrão Gerdau."
                placement="bottomLeft"
                children={
                  <div>
                    <Icon option="Info" variant="info" size="sm" />
                  </div>
                }
              />
            )}
          </RadioButtonContainer>
        ))}
      </FilterSection>
      <FilterSection title="Ordenação">
        {orderRadioButtons.map((radioBtn) => (
          <RadioButtonContainer key={radioBtn.value}>
            <RadioButton
              name={radioBtn.value}
              id={radioBtn.value}
              value={radioBtn.value}
              onChange={(e) =>
                radioBtn.value !== searchOptions.filters.ordenation &&
                handleDebounced(e.target.value as SortProductsProps['sortType'])
              }
              label={radioBtn.label}
              checked={radioBtn.value === searchOptions.filters.ordenation}
            />
          </RadioButtonContainer>
        ))}
      </FilterSection>
      <FilterSection title="Tipos de produto">
        {productsTypesList.map((c) => (
          <CheckboxContainer key={c.key}>
            <Checkbox
              name={c.description}
              ariaLabel={c.description}
              id={c.description}
              label={c.description}
              value={c.key}
              checked={shoundFilterBeChecked(c.key)}
              onChange={(e) => handleFilterChange(e, c.key)}
            />
          </CheckboxContainer>
        ))}
      </FilterSection>
    </FiltersContainer>
  );
};

interface FilterSectionProps {
  title: string;
}

const FilterSection = ({
  title,
  children
}: PropsWithChildren<FilterSectionProps>) => {
  return (
    <FilterContainer>
      <FilterTitle>{title}</FilterTitle>
      {children}
    </FilterContainer>
  );
};

export default Filters;
